import {
  Alert,
  App,
  Button,
  Empty,
  Popconfirm,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import type { GetUserPolicyGrantsResponse, IamUserPolicyGrantRecord } from '@/api/iam.api';
import { PolicyPermissionView } from '@/pages/iam/components/PolicyPermissionView';
import { useRevokePolicyGrant } from '@/pages/iam/composables/useGrantPolicyData';
import { usePolicyPermissionItems } from '@/pages/iam/composables/usePolicyPermissionItems';
import { fmtTime } from '@/utils/utils';
import './PrincipalGrants.css';

export interface PrincipalGrantsProps {
  identity: string;
  response?: GetUserPolicyGrantsResponse;
  loading?: boolean;
  error?: unknown;
  onOpenPolicyPreview: (policyId: string) => void;
  onAddGrant: () => void;
  onOpenGroupPreview?: (groupId: string) => void;
  emptyText?: string;
}

export default function PrincipalGrants({
  identity,
  response,
  loading = false,
  error,
  onOpenPolicyPreview,
  onAddGrant,
  onOpenGroupPreview,
  emptyText,
}: PrincipalGrantsProps) {
  const { message } = App.useApp();
  const revoke = useRevokePolicyGrant();
  const data = response?.data;

  const directRecords = data?.direct_authorization.records ?? [];
  const inheritedRecords = data?.group_inheritance.records ?? [];
  const permissionItems = usePolicyPermissionItems({
    actions: data?.permission_summary.actions,
  });

  function revokeGrant(record: IamUserPolicyGrantRecord) {
    revoke.mutate(
      {
        policy_ids: [record.policy_id],
        identities: [{ identity_type: 'user', identity }],
      },
      {
        onSuccess: () => message.success('授权已撤销'),
      }
    );
  }

  function renderRecord(record: IamUserPolicyGrantRecord, inherited: boolean) {
    return (
      <div className="iam-principal-grants-record" key={`${record.source_type}:${record.grant_id}`}>
        <div className="iam-principal-grants-record-main">
          <div className="iam-principal-grants-title">
            <Space size={8} wrap>
              <Button
                type="link"
                className="iam-principal-grants-link"
                onClick={() => onOpenPolicyPreview(record.policy_id)}
              >
                {record.policy_name || record.policy_id}
              </Button>
              {inherited ? (
                <Tag bordered={false}>{record.identity_name || record.identity}</Tag>
              ) : (
                <Tag bordered={false} color="purple">
                  直接授权
                </Tag>
              )}
            </Space>
          </div>
          <Typography.Text type="secondary">
            {record.policy_description || `${record.action_total} 个权限点`}
          </Typography.Text>
        </div>
        <div className="iam-principal-grants-record-audit">
          <Typography.Text style={{ color: 'var(--ant-color-text-description)' }}>
            {record.authorizer_name}
          </Typography.Text>
          <Typography.Text type="secondary">
            {fmtTime(new Date(record.authorization_at * 1000).toISOString())}
          </Typography.Text>
        </div>
        {inherited ? (
          <Tooltip title="继承权限需前往来源用户组管理">
            <Button
              type="text"
              aria-label="查看来源用户组"
              icon={<ExternalLink size={15} />}
              onClick={() => onOpenGroupPreview?.(record.identity)}
            />
          </Tooltip>
        ) : (
          <Popconfirm
            title="撤销这条授权？"
            description={`只撤销当前记录，将影响 1 名用户；其他直接或用户组授权不受影响。`}
            okText="撤销"
            okButtonProps={{ danger: true, loading: revoke.isPending }}
            onConfirm={() => revokeGrant(record)}
          >
            <Tooltip title="撤销授权">
              <Button
                type="text"
                danger
                aria-label={`撤销 ${record.policy_name || record.policy_id} 的授权`}
                icon={<Trash2 size={15} />}
              />
            </Tooltip>
          </Popconfirm>
        )}
      </div>
    );
  }

  function renderRecords(records: IamUserPolicyGrantRecord[], inherited = false) {
    if (!records.length) {
      return (
        <div className="iam-principal-grants-empty">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText || '暂无授权记录'} />
        </div>
      );
    }
    return (
      <div className="iam-principal-grants-list">
        {records.map((record) => renderRecord(record, inherited))}
      </div>
    );
  }

  const tabItems = [
    {
      key: 'summary',
      label: `权限汇总（${data?.permission_summary.action_total ?? 0} 个权限点）`,
      children: (
        <PolicyPermissionView
          items={permissionItems}
          emptyDescription="暂无有效权限"
          showFilters={false}
        />
      ),
    },
    {
      key: 'direct',
      label: `直接授权（${directRecords.length}）`,
      children: renderRecords(directRecords),
    },
    ...(inheritedRecords.length
      ? [
          {
            key: 'inherited',
            label: `用户组继承（${inheritedRecords.length}）`,
            children: renderRecords(inheritedRecords, true),
          },
        ]
      : []),
  ];

  return (
    <div className="iam-principal-grants-panel">
      <div className="iam-principal-grants-head">
        <Button type="primary" icon={<Plus size={14} />} onClick={onAddGrant}>
          添加权限
        </Button>
      </div>
      {error ? (
        <Alert
          type="error"
          showIcon
          message="用户权限加载失败"
          description={error instanceof Error ? error.message : String(error)}
        />
      ) : loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Tabs className="iam-principal-grants-tabs" defaultActiveKey="summary" items={tabItems} />
      )}
    </div>
  );
}
